import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Clock, CheckCircle2, XCircle, Landmark, RefreshCw } from 'lucide-react';

interface BankTransferRequest {
  id: string;
  plan: string;
  amount: number;
  currency: string;
  transfer_reference: string;
  transfer_description: string | null;
  status: 'pending' | 'approved' | 'rejected';
  admin_notes: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export default function PendingBankTransfer() {
  const navigate = useNavigate();
  const { currentOrganization } = useAppStore();

  const [request, setRequest] = useState<BankTransferRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const formatDate = (isoString: string) =>
    new Date(isoString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

  useEffect(() => {
    const fetchRequest = async () => {
      if (!currentOrganization) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('bank_transfer_requests')
          .select('id, plan, amount, currency, transfer_reference, transfer_description, status, admin_notes, reviewed_at, created_at')
          .eq('organization_id', currentOrganization.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (error && error.code !== 'PGRST116') throw error;
        setRequest(data ?? null);
      } catch (error) {
        console.error('Error fetching bank transfer request:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequest();
  }, [currentOrganization]);

  const getStatusBadge = (status: BankTransferRequest['status']) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50">
            <Clock className="w-3 h-3 mr-1" />
            Pending Review
          </Badge>
        );
      case 'approved':
        return (
          <Badge variant="outline" className="border-green-500 text-green-700 bg-green-50">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="border-red-400 text-red-700 bg-red-50">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        );
    }
  };

  const getStatusMessage = (status: BankTransferRequest['status'], adminNotes: string | null) => {
    switch (status) {
      case 'pending':
        return (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
            <div className="flex items-center gap-2 font-medium text-amber-900 mb-1">
              <Clock className="w-4 h-4" />
              Awaiting Verification
            </div>
            <p className="text-amber-700">
              Your transfer details have been received. Our team will verify your payment and
              activate your subscription within <strong>1–2 business days</strong>.
            </p>
          </div>
        );
      case 'approved':
        return (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm">
            <div className="flex items-center gap-2 font-medium text-green-900 mb-1">
              <CheckCircle2 className="w-4 h-4" />
              Payment Confirmed
            </div>
            <p className="text-green-700">
              Your bank transfer has been verified and your subscription is now active.
            </p>
            {adminNotes && (
              <p className="text-green-700 mt-2 border-t border-green-200 pt-2">
                <span className="font-medium">Note from admin:</span> {adminNotes}
              </p>
            )}
          </div>
        );
      case 'rejected':
        return (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm">
            <div className="flex items-center gap-2 font-medium text-red-900 mb-1">
              <XCircle className="w-4 h-4" />
              Transfer Not Verified
            </div>
            <p className="text-red-700">
              We could not verify your bank transfer. Please check your details and try again,
              or contact support for assistance.
            </p>
            {adminNotes && (
              <p className="text-red-700 mt-2 border-t border-red-200 pt-2">
                <span className="font-medium">Reason:</span> {adminNotes}
              </p>
            )}
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-lg mx-auto py-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="max-w-lg mx-auto py-12">
        <Card className="text-center">
          <CardContent className="pt-12 pb-8">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Landmark className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold mb-2">No Transfer Request Found</h2>
            <p className="text-muted-foreground mb-6">
              You haven't submitted a bank transfer request yet.
            </p>
            <Button onClick={() => navigate('/subscription')}>
              View Plans
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto py-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
          <Landmark className="w-5 h-5 text-muted-foreground" />
        </div>
        <div>
          <h1 className="text-xl font-bold">Bank Transfer Status</h1>
          <p className="text-sm text-muted-foreground">Track your manual payment review</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Transfer Request</CardTitle>
            {getStatusBadge(request.status)}
          </div>
          <CardDescription>
            Submitted on {formatDate(request.created_at)}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Plan</span>
              <span className="font-medium capitalize">{request.plan}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Amount</span>
              <span className="font-medium">
                {request.amount.toLocaleString()} {request.currency}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Transfer Reference</span>
              <span className="font-medium font-mono text-xs bg-muted px-2 py-0.5 rounded">
                {request.transfer_reference}
              </span>
            </div>
            {request.transfer_description && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground shrink-0">Notes</span>
                <span className="text-right">{request.transfer_description}</span>
              </div>
            )}
            {request.reviewed_at && (
              <>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reviewed on</span>
                  <span className="font-medium">{formatDate(request.reviewed_at)}</span>
                </div>
              </>
            )}
          </div>

          <Separator />

          {getStatusMessage(request.status, request.admin_notes)}

          <div className="flex flex-col gap-2 pt-2">
            {request.status === 'approved' && (
              <Button onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            )}
            {request.status === 'rejected' && (
              <Button onClick={() => navigate('/subscription')}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            )}
            {request.status === 'pending' && (
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
